/* eslint-disable react/prop-types */
import { Box } from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useOrganization } from "~hooks";
import { useCreateGAOrder } from "../../nonseated.hooks";
import { useNonSeatedReservationStore } from "../../store/non-seated-reservation-store";
import { animation } from "./ExperienceSalesFooter.animation";
import { IExperienceSalesLayout } from "./types";
import { useMaybeGetTicketExchangeData, useFooterActions } from "./hooks";
import { getButtonText, reservationStickyFooter } from "./utils";
import {
  ErrorModal,
  FooterActionBar,
  PriceInfo,
  TicketExchangeSection,
} from "./components";
import { QuantitySetValidationModal } from "~components/reservation/common/common.QuantitySetValidation.modal";
import { isPrechargeFlowAtom } from "~features/nonseated-reservation/isPrechargeFlowAtom";
import { useAtom } from "jotai";
import { PrechargeSubmitModal } from "./components/PrechargeSubmitModal";

const MotionBox = motion(Box);

export const ExperienceSalesFooter = ({
  id,
  releaseId,
  membershipId,
  children,
  timezone,
}: IExperienceSalesLayout) => {
  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);
  // Used for precharge flow to redirect to checkout after the animation
  const [orderId, setOrderId] = useState<string | null>(null);

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);
  const { hasFeature, organization } = useOrganization();

  // Get ticket exchange data
  const {
    ownedTicketData,
    hasOwnedTickets: isExchangeFlow,
    ticketsLoading,
    hasTickets,
    ownedTicketsExchangeValue,
  } = useMaybeGetTicketExchangeData(organization?.id);

  // Get state from order creation hook
  const { handleGAOrderCreation } = useCreateGAOrder({
    releaseId,
    membershipId,
    eventId: id,
  });

  const selectedTickets = useNonSeatedReservationStore(
    (state) => state.selectedTicketTypes[id] ?? []
  );

  const cart = useNonSeatedReservationStore((state) => state.totals[id]);

  // Get the global submitting state from the store
  const isStoreSubmitting = useNonSeatedReservationStore(
    (state) => state.isSubmitting
  );

  // Set up footer actions
  const {
    submitting,
    alertError,
    setAlertError,
    handlePrimaryAction,
  } = useFooterActions({
    handleGAOrderCreation,
    selectedTickets,
    multibuyId: cart?.multibuyId,
    isExchangeFlow,
    ownedTicketData, // Pass the owned ticket data to the hook
    onOrderCreated: (id) => {
      setOrderId(id);
    },
  });

  const hasSelection = selectedTickets.length > 0;
  const showFees = hasFeature("showHiddenFees");

  // Only show footer when either tickets are selected or in exchange flow
  const showFooter =
    !hasFeature("LegacyGATicketSelection") && (hasSelection || isExchangeFlow);

  // Get button text based on current state
  const buttonText = getButtonText(
    isExchangeFlow,
    hasSelection,
    isPrechargeFlow
  );

  const onCloseQuantitySetValidationModal = () => {
    setQuantitySetValidationError(null);
  };

  const validateQuantitySets = (
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    for (const ticketType of selectedTickets) {
      if (!ticketType?.quantitySet) continue;

      const isValid = ticketType.quantity % ticketType.quantitySet === 0;

      if (!isValid) {
        setQuantitySetValidationError({
          name: ticketType.name,
          quantitySet: ticketType.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = () => {
    if (!selectedTickets) return;

    const isValid = validateQuantitySets(setQuantitySetValidationError);

    if (!isValid) return;
    void handlePrimaryAction();
  };

  if (!cart) {
    return null;
  }
  return (
    <>
      <Box width="100%">
        {children}
        <AnimatePresence>
          {showFooter && (
            <MotionBox
              {...animation}
              position={
                reservationStickyFooter(organization?.id) ? "fixed" : undefined
              }
              zIndex="sticky"
              bottom={0}
              right={0}
              left={0}
              width={1}
              bg="white"
              boxShadow="card"
            >
              <Box
                flex={1}
                px={2}
                py={3}
                maxW="960px"
                mx={"auto" as any}
                minH={80}
              >
                <TicketExchangeSection
                  isExchangeFlow={isExchangeFlow}
                  ticketsLoading={ticketsLoading}
                  hasTickets={hasTickets}
                  ownedTicketData={ownedTicketData}
                  ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                />

                <FooterActionBar
                  priceInfo={
                    <PriceInfo
                      isExchangeFlow={isExchangeFlow}
                      ticketsLoading={ticketsLoading}
                      hasSelection={hasSelection}
                      cart={cart}
                      ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                      showFees={showFees}
                      timezone={timezone}
                    />
                  }
                  buttonText={buttonText}
                  onAction={handleBuyButtonClicked}
                  isLoading={submitting || isStoreSubmitting}
                  isDisabled={
                    !hasSelection ||
                    (isExchangeFlow && ticketsLoading) ||
                    isStoreSubmitting
                  }
                />
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
        <QuantitySetValidationModal
          isOpen={!!quantitySetValidationError}
          onClose={onCloseQuantitySetValidationModal}
          quantitySet={quantitySetValidationError?.quantitySet}
          name={quantitySetValidationError?.name}
        />
      </Box>

      {/* Precharge only loading / error modal */}
      {isPrechargeFlow && (
        <PrechargeSubmitModal
          isOpen={!!alertError || submitting || isStoreSubmitting || !!orderId}
          orderId={orderId}
          error={alertError}
          onClose={() => {
            setAlertError(undefined);
          }}
        />
      )}

      {!isPrechargeFlow && (
        <ErrorModal
          error={alertError}
          onClose={() => setAlertError(undefined)}
        />
      )}
    </>
  );
};
